import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swiper from 'swiper';
import { layoutsContent } from './Layouts.content';
import styles from './Layouts.module.sass';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { Popup } from '../Popup/Popup';
import arrow from '../HalfSlider/assets/arrow.svg';
import 'swiper/dist/css/swiper.min.css';

function CheckIcon({ selected }) {
  if (selected) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="12" fill="#FF8F57" />
        <path
          d="M7 12.5L10.2 15.7L17 8.9"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="11" stroke="rgba(255, 255, 255, 0.35)" strokeWidth="1" />
    </svg>
  );
}

export function Layouts() {
  const { tabs, ctaLabel } = layoutsContent;
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? '');
  const [activePage, setActivePage] = useState(0);
  const [selectedCardId, setSelectedCardId] = useState(tabs[0]?.pages[0]?.cards[0]?.id ?? null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const containerRef = useRef(null);
  const swiperRef = useRef(null);

  const activeTabData = useMemo(
    () => tabs.find((tab) => tab.id === activeTab) ?? tabs[0],
    [tabs, activeTab]
  );
  const pages = useMemo(() => activeTabData?.pages ?? [], [activeTabData]);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const pageCount = pages.length;

  const selectedLayout = useMemo(() => {
    const tab = tabs.find((item) => item.id === activeTab);
    if (!tab) return null;

    for (const page of tab.pages) {
      const card = page.cards.find((item) => item.id === selectedCardId);
      if (card) {
        return {
          rooms: tab.label,
          title: card.title.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
          area: card.tag,
        };
      }
    }

    return null;
  }, [tabs, activeTab, selectedCardId]);

  useEffect(() => {
    setActivePage(0);
    setSelectedCardId(activeTabData?.pages[0]?.cards[0]?.id ?? null);
  }, [activeTab, activeTabData]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (swiperRef.current) {
      swiperRef.current.destroy(true, true);
      swiperRef.current = null;
    }

    if (pageCount === 0) return;

    swiperRef.current = new Swiper(containerRef.current, {
      slidesPerView: 1,
      speed: 700,
      autoHeight: true,
      allowTouchMove: pageCount > 1,
      on: {
        init() {
          requestAnimationFrame(() => this.updateAutoHeight());
        },
        slideChange() {
          setActivePage(this.activeIndex);
          const page = pagesRef.current[this.activeIndex];
          if (page?.cards[0]) setSelectedCardId(page.cards[0].id);
        },
        slideChangeTransitionEnd() {
          this.updateAutoHeight();
        },
      },
    });

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, [activeTab, pageCount]);

  useEffect(() => {
    if (!swiperRef.current) return;
    const id = requestAnimationFrame(() => swiperRef.current?.updateAutoHeight());
    return () => cancelAnimationFrame(id);
  }, [activePage, activeTab, pages]);

  useEffect(() => {
    if (!isPopupOpen) return undefined;

    const { documentElement: html, body } = document;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [isPopupOpen]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  const handlePrev = useCallback(() => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (swiperRef.current) swiperRef.current.slideNext();
  }, []);

  return (
    <section className={styles.layouts}>
      <Container>
        <div className={styles.layouts__header}>
          <div className={styles.layouts__left}>
            <div
              className={styles.layouts__title}
              dangerouslySetInnerHTML={{ __html: layoutsContent.title }}
            />
            <div
              className={styles.layouts__text}
              dangerouslySetInnerHTML={{ __html: layoutsContent.text }}
            />
          </div>
          <div className={styles.layouts__line}></div>
          <div
            className={styles.layouts__description}
            dangerouslySetInnerHTML={{ __html: layoutsContent.description }}
          />
        </div>

        <div className={styles.layouts__block}>
          <div className={styles.layouts__toolbar}>
            <div className={styles.layouts__tabs}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`${styles.layouts__tab} ${
                    tab.id === activeTab ? styles['layouts__tab--active'] : ''
                  }`}
                  onClick={() => handleTabClick(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {pageCount > 1 && (
              <div className={styles.layouts__controls}>
                <span className={styles.layouts__pagination}>
                  <span className={styles.layouts__paginationCurrent}>{activePage + 1}</span>
                  <span className={styles.layouts__paginationTotal}> / {pageCount}</span>
                </span>
                <div className={styles.layouts__nav}>
                  <div className={styles.layouts__navBtn} onClick={handlePrev}>
                    <img src={arrow} alt="" className={styles.layouts__navIcon} />
                  </div>
                  <div className={styles.layouts__navBtn} onClick={handleNext}>
                    <img
                      src={arrow}
                      alt=""
                      className={`${styles.layouts__navIcon} ${styles['layouts__navIcon--right']}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            key={activeTab}
            className={`swiper-container ${styles.layouts__swiper}`}
            ref={containerRef}
          >
            <div className="swiper-wrapper">
              {pages.map((page, pageIndex) => (
                <div key={`${activeTab}-${pageIndex}`} className="swiper-slide">
                  <div className={styles.layouts__slide}>
                    {page.typeTitle ? (
                      <div className={styles.layouts__typeTitle}>{page.typeTitle}</div>
                    ) : null}
                    <div className={styles.layouts__cards}>
                      {page.cards.map((card) => {
                        const isSelected = card.id === selectedCardId;
                        return (
                          <button
                            key={card.id}
                            type="button"
                            className={`${styles.layouts__card} ${
                              isSelected ? styles['layouts__card--selected'] : ''
                            }`}
                            onClick={() => setSelectedCardId(card.id)}
                          >
                            <div className={styles.layouts__cardPlan}>
                              <div className={styles.layouts__cardPlanImage}>
                                <img src={card.image} alt="" />
                              </div>
                            </div>
                            <div className={styles.layouts__cardInfo}>
                              <div className={styles.layouts__cardTag}>{card.tag}</div>
                              <div
                                className={styles.layouts__cardTitle}
                                dangerouslySetInnerHTML={{ __html: card.title }}
                              />
                              <span className={styles.layouts__cardCheck}>
                                <CheckIcon selected={isSelected} />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.layouts__footer}>
            <Button
              className={styles.layouts__cta}
              label={ctaLabel}
              onClick={() => setIsPopupOpen(true)}
            />
          </div>
        </div>

        {isPopupOpen && (
          <Popup selectedLayout={selectedLayout} onClose={() => setIsPopupOpen(false)} />
        )}

        <div className={styles.layouts__list}>
          {layoutsContent.list.map((item, i) => (
            <div key={i} className={styles.features__item}>
              <img src={item.image} alt="" className={styles.layouts__image} />
              <div
                className={styles.layouts__itemTitle}
                dangerouslySetInnerHTML={{ __html: item.itemTitle }}
              />
              <div
                className={styles.layouts__itemText}
                dangerouslySetInnerHTML={{ __html: item.itemText }}
              />
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
